import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useGameStore } from '@/store/gameStore';
import { generateAuctionPool, getAllVerses, getVerseBackdrop, getVerseThumbnail } from '@/data/characters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { BrandCorner } from '@/components/BrandCorner';
import {
  createMatch,
  joinMatch,
  subscribeToMatch,
  subscribeToPlayers,
  updateDisplayName,
  updateMatchSettings,
  updateMatchStatus,
} from '@/lib/multiplayer';

export function LobbyScreen() {
  const {
    settings,
    updateSettings,
    setPhase,
    setRoomCode,
    setMultiplayerContext,
    resetMultiplayerGameState,
    clearMultiplayerContext,
    applyMatchSnapshot,
    applyPlayersSnapshot,
    opponent,
    roomCode: storedRoomCode,
    matchId: storedMatchId,
    playerId: storedPlayerId,
    playerRole: storedPlayerRole,
  } = useGameStore();
  const [roomCode, setLocalRoomCode] = useState('');
  const [lobbyMode, setLobbyMode] = useState<'host' | 'join'>('host');
  const [joinCode, setJoinCode] = useState('');
  const [hostName, setHostName] = useState('Host');
  const [guestName, setGuestName] = useState('Guest');
  const [matchId, setMatchId] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [guestJoined, setGuestJoined] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const verses = useMemo(
    () => getAllVerses().filter((verse) => !verse.startsWith('Creator:')),
    []
  );
  const startingCoinOptions = [3000, 5000, 8000, 10000];
  const auctionCountOptions: Array<10 | 15 | 20 | 30> = [10, 15, 20, 30];
  const roomCodeReady = roomCode.length === 6;

  const isHost = lobbyMode === 'host';
  const hostReady = Boolean(matchId);
  const settingsLocked = guestJoined;
  const settingsDisabled = settingsLocked;

  const auctionPool = useMemo(
    () => generateAuctionPool(settings.auctionCount, settings.verse),
    [settings.verse, settings.auctionCount]
  );

  useEffect(() => {
    if (!settings.verse.startsWith('Creator:')) return;
    updateSettings({ verse: verses[0] ?? 'Naruto' });
  }, [settings.verse, updateSettings, verses]);

  useEffect(() => {
    if (matchId || !storedMatchId || !storedRoomCode || !storedPlayerId) return;
    setLocalRoomCode(storedRoomCode);
    setRoomCode(storedRoomCode);
    setMatchId(storedMatchId);
    setPlayerId(storedPlayerId);
    if (storedPlayerRole === 'host') setLobbyMode('host');
  }, [matchId, storedMatchId, storedRoomCode, storedPlayerId, storedPlayerRole, setRoomCode]);

  useEffect(() => {
    let unsubMatch: (() => void) | null = null;
    let unsubPlayers: (() => void) | null = null;

    if (roomCodeReady && matchId) {
      unsubMatch = subscribeToMatch(matchId, (match) => {
        applyMatchSnapshot(match);
      });
      unsubPlayers = subscribeToPlayers(matchId, (players) => {
        applyPlayersSnapshot(players);
        const hasGuest = players.some((p) => p.player_role === 'guest');
        setGuestJoined(hasGuest);
      });
    }

    return () => {
      if (unsubMatch) unsubMatch();
      if (unsubPlayers) unsubPlayers();
    };
  }, [roomCodeReady, roomCode, matchId, applyMatchSnapshot, applyPlayersSnapshot]);

  const handleCreateRoom = async () => {
    if (!isHost || matchId || isWorking) return;
    resetMultiplayerGameState();
    setIsWorking(true);
    setError(null);
    const result = await createMatch(hostName.trim() || 'Host', settings, auctionPool);
    setIsWorking(false);
    if (!result) {
      setError('Could not create match. Check Supabase connection.');
      return;
    }

    setLocalRoomCode(result.match.room_code);
    setRoomCode(result.match.room_code);
    setMatchId(result.match.id);
    setPlayerId(result.playerId);
    setMultiplayerContext({
      matchId: result.match.id,
      playerId: result.playerId,
      playerRole: 'host',
    });
    toast.success(`Room created. Share code: ${result.match.room_code}`);
  };

  useEffect(() => {
    if (lobbyMode !== 'join') return;
    setLocalRoomCode('');
    setRoomCode('');
    setMatchId(null);
    setPlayerId(null);
    setGuestJoined(false);
    setError(null);
    clearMultiplayerContext();
  }, [lobbyMode, setRoomCode, clearMultiplayerContext]);

  useEffect(() => {
    if (!isHost) return;
    if (!matchId || !roomCodeReady) return;
    if (settingsLocked) return;

    updateMatchSettings(matchId, {
      verse: settings.verse,
      starting_coins: settings.startingCoins,
      bid_timer: settings.bidTimer,
      battle_format: settings.battleFormat,
      auction_pool_ids: auctionPool.map((character) => character.id),
      hidden_stat: settings.hiddenStat,
      rebid_mode: settings.rebidMode,
      coin_rewards_enabled: settings.coinRewards,
      coin_reward_amount: settings.coinRewardAmount,
      });
  }, [
    isHost,
    matchId,
    roomCodeReady,
    roomCode,
    settings,
    settingsLocked,
    auctionPool,
  ]);

  const handleStartHost = async () => {
    if (!roomCodeReady || !matchId) return;
    const result = await updateMatchStatus(matchId, 'auction');
    if (!result.ok) {
      toast.error('Could not start match. Try again.');
    }
  };

  const handleJoinGame = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    setIsWorking(true);
    setError(null);
    resetMultiplayerGameState();
    const result = await joinMatch(code, guestName.trim() || 'Guest');
    setIsWorking(false);
    if (!result) {
      setError('Connection error. Check your internet and try again.');
      return;
    }
    if (!result.ok) {
      setError(result.error);
      return;
    }

    setLocalRoomCode(result.match.room_code);
    setRoomCode(result.match.room_code);
    setMatchId(result.match.id);
    setPlayerId(result.playerId);
    setMultiplayerContext({
      matchId: result.match.id,
      playerId: result.playerId,
      playerRole: 'guest',
    });
  };

  useEffect(() => {
    if (!matchId || !playerId) return;
    const name = isHost ? hostName : guestName;
    const trimmed = name.trim();
    if (!trimmed) return;
    updateDisplayName(matchId, trimmed, playerId);
  }, [matchId, playerId, hostName, guestName, isHost]);

  return (
    <div className="min-h-screen bg-arena flex flex-col items-center justify-center p-6">
      <BrandCorner />
      <motion.div
        className="w-full max-w-4xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex justify-center gap-2 mb-6">
          <Button
            variant={lobbyMode === 'host' ? 'default' : 'outline'}
            onClick={() => setLobbyMode('host')}
          >
            Host Game
          </Button>
          <Button
            variant={lobbyMode === 'join' ? 'default' : 'outline'}
            onClick={() => setLobbyMode('join')}
          >
            Join Game
          </Button>
        </div>

        {lobbyMode === 'host' ? (
          <>
            <div className="text-center mb-8">
              <p className="text-sm text-muted-foreground mb-2">Room Code</p>
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-secondary border border-border">
                <span className="font-mono font-bold text-3xl text-primary tracking-widest">
                  {roomCodeReady ? roomCode : '......'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {roomCodeReady ? 'Share this code with a friend' : 'Create a room to get a code'}
              </p>
              {!matchId && (
                <div className="mt-4">
                  <Button
                    onClick={handleCreateRoom}
                    disabled={isWorking}
                    className="font-display font-bold"
                  >
                    {isWorking ? 'Creating...' : 'Create Room'}
                  </Button>
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-xl bg-destructive/10 border border-destructive/40 p-3 text-sm text-destructive mb-6">
                {error}
              </div>
            )}

            <div className={`space-y-6 mb-8 ${settingsDisabled ? 'opacity-60' : ''}`}>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Display Name</label>
                <Input
                  value={hostName}
                  onChange={(event) => setHostName(event.target.value)}
                  maxLength={24}
                  disabled={settingsLocked}
                  className="max-w-xs"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Verse</label>
                <div className="grid grid-cols-4 gap-4 justify-items-center px-2">
                  {verses.map((verse) => {
                    const backdrop = getVerseThumbnail(verse);
                    const selected = settings.verse === verse;
                    return (
                      <button
                        key={verse}
                        type="button"
                        onClick={() => updateSettings({ verse })}
                        aria-label={verse}
                        title={verse}
                        disabled={settingsLocked || settingsDisabled}
                        className={`relative h-20 w-32 rounded-xl overflow-hidden border-2 transition-all ${
                          selected ? 'border-primary ring-2 ring-primary/40' : 'border-border/60'
                        } ${settingsLocked || settingsDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <span className="sr-only">{verse}</span>
                        <img
                          src={backdrop}
                          alt={verse}
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                        <div className={`absolute inset-0 ${selected ? 'bg-black/5' : 'bg-black/10'} transition-colors`} />
                        {selected && (
                          <div className="absolute inset-0 ring-2 ring-primary/60 rounded-xl" />
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent py-1 px-2">
                          <p
                            className={`text-xs font-display font-semibold text-center ${
                              selected ? 'text-primary' : 'text-white/90'
                            }`}
                          >
                            {verse}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm text-muted-foreground mb-2">Bid Timer</label>
                <div className="flex gap-2">
                  {[15, 20, 30].map((time) => (
                    <Button
                      key={time}
                      variant={settings.bidTimer === time ? 'default' : 'outline'}
                      onClick={() => updateSettings({ bidTimer: time })}
                      disabled={settingsLocked || settingsDisabled}
                    >
                      {time}s
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-muted-foreground mb-2">Battle Format</label>
                <div className="flex gap-2">
                  {([3, 5, 10] as const).map((format) => (
                    <Button
                      key={format}
                      variant={settings.battleFormat === format ? 'default' : 'outline'}
                      onClick={() => updateSettings({ battleFormat: format })}
                      disabled={settingsLocked || settingsDisabled}
                    >
                      First to {format}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between max-w-xs">
                <div>
                  <label className="block text-sm text-muted-foreground">Hidden Stat Mode</label>
                  <p className="text-xs text-muted-foreground">Stat revealed after you lock pick.</p>
                </div>
                <Switch
                  checked={settings.hiddenStat}
                  onCheckedChange={(checked) => updateSettings({ hiddenStat: checked })}
                  disabled={settingsLocked || settingsDisabled}
                />
              </div>

              <div className="flex items-center justify-between max-w-xs">
                <div>
                  <label className="block text-sm text-muted-foreground">Rebid Ties</label>
                  <p className="text-xs text-muted-foreground">Ties trigger a rebid instead of skip.</p>
                </div>
                <Switch
                  checked={settings.rebidMode}
                  onCheckedChange={(checked) => updateSettings({ rebidMode: checked })}
                  disabled={settingsLocked || settingsDisabled}
                />
              </div>

              <div className="flex items-center justify-between max-w-xs">
                <div>
                  <label className="block text-sm text-muted-foreground">Coin Rewards</label>
                  <p className="text-xs text-muted-foreground">Award coins for winning battle rounds.</p>
                </div>
                <Switch
                  checked={settings.coinRewards}
                  onCheckedChange={(checked) => updateSettings({ coinRewards: checked })}
                  disabled={settingsLocked || settingsDisabled}
                />
              </div>

              {settings.coinRewards && (
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Coins per Battle Win</label>
                  <div className="flex flex-wrap items-center gap-2">
                    {[250, 500, 750, 1000].map((amount) => (
                      <Button
                        key={amount}
                        variant={settings.coinRewardAmount === amount ? 'default' : 'outline'}
                        onClick={() => updateSettings({ coinRewardAmount: amount })}
                        disabled={settingsLocked || settingsDisabled}
                      >
                        {amount}
                      </Button>
                    ))}
                    <Input
                      type="number"
                      min={0}
                      step={50}
                      value={settings.coinRewardAmount}
                      onChange={(event) => {
                        const parsed = Number(event.target.value);
                        if (!Number.isNaN(parsed)) {
                          updateSettings({ coinRewardAmount: Math.max(0, parsed) });
                        }
                      }}
                      disabled={settingsLocked || settingsDisabled}
                      className="w-28 text-center font-mono"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm text-muted-foreground mb-2">Starting Coins</label>
                <div className="flex flex-wrap items-center gap-2">
                  {startingCoinOptions.map((amount) => (
                    <Button
                      key={amount}
                      variant={settings.startingCoins === amount ? 'default' : 'outline'}
                      onClick={() => updateSettings({ startingCoins: amount })}
                      disabled={settingsLocked || settingsDisabled}
                    >
                      {amount}
                    </Button>
                  ))}
                  <Input
                    type="number"
                    min={1000}
                    step={500}
                    value={settings.startingCoins}
                    onChange={(event) => {
                      const parsed = Number(event.target.value);
                      if (!Number.isNaN(parsed)) {
                        updateSettings({ startingCoins: Math.max(0, parsed) });
                      }
                    }}
                    disabled={settingsLocked || settingsDisabled}
                    className="w-28 text-center font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-muted-foreground mb-2">Auction Count</label>
                <div className="flex flex-wrap items-center gap-2">
                  {auctionCountOptions.map((count) => (
                    <Button
                      key={count}
                      variant={settings.auctionCount === count ? 'default' : 'outline'}
                      onClick={() => updateSettings({ auctionCount: count })}
                      disabled={settingsLocked || settingsDisabled}
                    >
                      {count}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-secondary/50 border border-border p-4 text-sm text-muted-foreground mb-6">
              <p className="font-display font-semibold text-foreground mb-2">Settings Summary</p>
              <div className="flex flex-wrap gap-3">
                <span>Verse: <span className="text-foreground">{settings.verse}</span></span>
                <span>Bid: <span className="text-foreground">{settings.bidTimer}s</span></span>
                <span>Best of: <span className="text-foreground">{settings.battleFormat}</span></span>
                <span>Coins: <span className="text-foreground">{settings.startingCoins}</span></span>
                <span>Auctions: <span className="text-foreground">{settings.auctionCount}</span></span>
                <span>Hidden Stat: <span className="text-foreground">{settings.hiddenStat ? 'On' : 'Off'}</span></span>
                <span>Rebid: <span className="text-foreground">{settings.rebidMode ? 'On' : 'Off'}</span></span>
                {settings.coinRewards && (
                  <span>Coin Rewards: <span className="text-foreground">{settings.coinRewardAmount} per win</span></span>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center p-4 rounded-xl bg-secondary/50 border border-border mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-player/20 border border-player flex items-center justify-center font-mono text-sm">
                  P1
                </div>
                <div>
                  <p className="font-display font-semibold text-player">You</p>
                  <p className="text-xs text-muted-foreground">Host</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div>
                  <p className="font-display font-semibold text-muted-foreground text-right">
                    {guestJoined ? opponent.name : 'Waiting...'}
                  </p>
                  <p className="text-xs text-muted-foreground text-right">Guest</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-muted border border-border flex items-center justify-center font-mono text-sm">
                  P2
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setPhase('home')}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleStartHost}
                disabled={!hostReady || !guestJoined || isWorking}
                className="flex-1 font-display font-bold"
              >
                Start vs Player
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-6 mb-8">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Enter Room Code</p>
              <div className="flex items-center gap-3 justify-center">
                <Input
                  value={joinCode}
                  onChange={(event) => setJoinCode(event.target.value.toUpperCase())}
                  placeholder="ABC123"
                  maxLength={6}
                  disabled={Boolean(matchId)}
                  className="w-56 text-center font-mono tracking-widest"
                />
                <Button
                  onClick={handleJoinGame}
                  disabled={!joinCode.trim() || isWorking || Boolean(matchId)}
                  className="font-display font-bold"
                >
                  Join
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">You will use the host's settings.</p>
            </div>

            <div>
              <label className="block text-sm text-muted-foreground mb-2 text-center">
                Display Name
              </label>
              <div className="flex justify-center">
                <Input
                  value={guestName}
                  onChange={(event) => setGuestName(event.target.value)}
                  maxLength={24}
                  disabled={Boolean(matchId)}
                  className="w-56 text-center"
                />
              </div>
            </div>

            {matchId && (
              <div className="rounded-xl bg-secondary/50 border border-border p-4 text-sm text-muted-foreground text-center">
                Joined room {roomCode}. Waiting for host to start...
              </div>
            )}

            {error && (
              <div className="rounded-xl bg-destructive/10 border border-destructive/40 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setPhase('home')}
                className="flex-1"
              >
                Back
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
